import { updateIDs } from './copied/getIds';
import queryClient from './services/queryClient';
import { rootContainer } from './shadowRoot';
import { PosthogEventName } from './copied/constants/PosthogEventName';

const tAIlorReadyPromise = new Promise<void>((resolve) => {
  rootContainer.addEventListener(
    'tailor:ready',
    () => {
      resolve();
    },
    { once: true }
  );
});

const toggleOpen = async (isOpen?: boolean, eventData: object = {}) => {
  await tAIlorReadyPromise;
  rootContainer.dispatchEvent(new CustomEvent('tailor:toggle', { detail: isOpen }));
  const captureEvent = window?.ostro?.analytics?.captureEvent ?? (() => {});
  captureEvent(PosthogEventName.ToggleOpenCalled, { isOpen, ...eventData });
};

export class TAIlor {
  private source: string;
  private configuration?: object;

  constructor(source: string) {
    this.source = source;
  }

  exposeTAIlorConfiguration(data: object) {
    this.configuration = data;
  }

  get configurationData() {
    return this.configuration;
  }

  public async reloadWithId(newConditionId: string, conditionChangedEventDetails: object = {}) {
    const prevConditionId = window.ostro.conditionId;

    await tAIlorReadyPromise;

    if (newConditionId === prevConditionId) return;

    try {
      window.ostro.conditionId = newConditionId;

      await updateIDs();
      queryClient.invalidateQueries();

      const detail = {
        to: '/tailor',
        options: { state: { skipAnimation: true } },
      };

      rootContainer.dispatchEvent(new CustomEvent('tailor:navigate', { detail }));

      rootContainer.dispatchEvent(
        new CustomEvent('tailor:condition-changed', {
          detail: { ...conditionChangedEventDetails, conditionId: newConditionId },
        })
      );
    } catch {
      // If the refetch fails, revert the condition ID
      window.ostro.conditionId = prevConditionId;
      updateIDs();
      queryClient.invalidateQueries();
    }

    const captureEvent = window?.ostro?.analytics?.captureEvent ?? (() => {});
    captureEvent(PosthogEventName.ReloadWithIdCalled, { id: newConditionId });
  }

  public toggleOpen(isOpen?: boolean) {
    toggleOpen(isOpen, { source: this.source });
  }

  public setExternalId(id: string) {
    rootContainer.dispatchEvent(new CustomEvent('tailor:set-external-id', { detail: id }));
  }

  public handleAimSessionId() {
    const maxAttempts = 100;
    let attempts = 0;
    const intervalId = setInterval(() => {
      if (window.aim_session_id) {
        this.setExternalId(window.aim_session_id);
        clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
      attempts++;
    }, 100);
  }

  public navigate(url: string) {
    rootContainer.dispatchEvent(new CustomEvent('tailor:navigate', { detail: { to: url } }));
  }
}

class RxDefineChat {
  toggleOpen(isOpen?: boolean) {
    toggleOpen(isOpen, { source: 'RxDefineChat' });
  }
  hideChat() {
    // mock function
  }
  setClosingMode() {}
  onClose() {}
}

export function exposeTAIlor() {
  Object.defineProperty(window, 'TAIlor', {
    value: new TAIlor('TAIlor'),
  });

  Object.defineProperty(window, 'OstroChat', {
    value: new TAIlor('OstroChat'),
  });

  // for legacy chat support
  // https://rxdefine.atlassian.net/browse/PEP-8538
  Object.defineProperty(window, 'RxDefineChat', {
    value: new RxDefineChat(),
  });
}
